import React, { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalBootstrap from "components/bootstrap/ModalBootstrap";
import { FileEarmarkSpreadsheet, InfoCircle } from "react-bootstrap-icons";
import {
  Button,
  Form,
  OverlayTrigger,
  Popover,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { ERROR_MESSAGE } from "components/Reports/reportAssets/assets";
import {
  useLoadChangeDecisionsExcelMutation,
  useLoadChangeStatusesExcelMutation,
} from "features/api/reports/reportsSlice";

type MassLoadingProps = {
  handleClose: () => void;
};

type massResultsCounts = {
  reportCompleted: number;
  reportFailedIds: number[];
};

const MassLoading: FC<MassLoadingProps> = ({ handleClose }) => {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  const [modalShow, setModalShow] = useState(true);
  const [isStatuses, setIsStatuses] = useState(true);
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);
  const [res, setRes] = useState<massResultsCounts>();
  const [loadChangeStatusesExcel] = useLoadChangeStatusesExcelMutation();
  const [loadChangeDecisionsExcel] = useLoadChangeDecisionsExcelMutation();

  const handleUploadFile = async (event) => {
    setIsLoadingUploadFile(true);
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("exelFile", file);

      try {
        const response = isStatuses
          ? await loadChangeStatusesExcel(formData).unwrap()
          : await loadChangeDecisionsExcel(formData).unwrap();
        setRes(response);
      } catch (error) {
        console.error("Error:", error);
        toast.error(ERROR_MESSAGE);
      } finally {
        setIsLoadingUploadFile(false);
        fileInput.current.value = null; // reset the input to allow another request with same file (refers to reduxTK - cache)
      }
    }
  };

  return (
    <ModalBootstrap
      dialogClassName="mass-loading-modal"
      onHide={() => {
        if (!isLoadingUploadFile) {
          setModalShow(false);
          handleClose();
        }
      }}
      show={modalShow}
      title={t("massLoading")}
      icon={<FileEarmarkSpreadsheet size={24} />}
      isWithFooter={false}
      backdrop="static" // prevent close on click outside
    >
      {res ? (
        <>
          <div className="label">
            {t("succesReports")}:
            <span className="value">{res?.reportCompleted}</span>
          </div>

          {res?.reportFailedIds?.length > 0 && (
            <>
              <div className="label">
                {t("failedReports")}:
                <span className="value">{res?.reportFailedIds?.length}</span>
              </div>

              {/* Failed Report IDs */}
              <div className="label">
                {t("failedReportIDs")}

                <OverlayTrigger
                  trigger="click"
                  placement="bottom"
                  overlay={
                    <Popover id="failed-ids-popover">
                      <Popover.Header as="h1">
                        {t("failedReportIDs")} ({res.reportFailedIds.length})
                      </Popover.Header>

                      <Popover.Body>
                        {res.reportFailedIds.map((id) => (
                          <div key={id}>{id}</div>
                        ))}
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <InfoCircle className="info" />
                </OverlayTrigger>
              </div>
            </>
          )}
        </>
      ) : (
        <Form className="massExcelForm">
          <h3>{t("chooseFileType")}</h3>

          <div className="flex-row">
            <Form.Check
              type="radio"
              id="statusRadio"
              value="status"
              checked={isStatuses}
              onChange={() => {
                setIsStatuses(true);
              }}
              disabled={isLoadingUploadFile}
            />
            <Form.Check.Label htmlFor="statusRadio">
              {t("changeStatus")}
            </Form.Check.Label>
          </div>
          <div className="flex-row">
            <Form.Check
              type="radio"
              id="decisionRadio"
              value="decision"
              checked={!isStatuses}
              onChange={() => {
                setIsStatuses(false);
              }}
              disabled={isLoadingUploadFile}
            />
            <Form.Check.Label htmlFor="decisionRadio">
              {t("changeDecision")}
            </Form.Check.Label>
          </div>

          <Button
            variant="primary"
            className="updates-modal-button"
            onClick={() => fileInput.current.click()}
            disabled={isLoadingUploadFile}
          >
            {isLoadingUploadFile ? (
              <Spinner animation="border" variant="secondary" />
            ) : (
              t("selectFile")
            )}
          </Button>
          <input
            type="file"
            accept=".xlsx, .xls"
            ref={fileInput}
            onChange={handleUploadFile}
          />
        </Form>
      )}
    </ModalBootstrap>
  );
};
export default MassLoading;
